import React, { useState, useRef, useCallback, useEffect } from 'react';
import BlobBot, { BotState } from '../Bots/BlobBot';
import EOS from '../../agent/persona';
import { STAGE, stageAlpha } from '../../design/lab/palette';
import { useReducedMotion } from '../../animations';

interface Message {
  id: string;
  type: 'command' | 'output' | 'error' | 'system' | 'trace' | 'states' | 'tools' | 'chart' | 'player' | 'context_ir' | 'multi_path';
  chart?: any;
  ir?: any;
  text: string;
  voice?: boolean;
}

interface VoiceTabProps {
  addMessage: (msg: Omit<Message, 'id'>) => void;
  callApi: (text: string) => Promise<string>;
  loading: boolean;
  agentState: BotState;
  // Compressed recent chat history for voice context
  voiceHistory?: Array<{ role: string; content: string }>;
}

type VoiceState = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

const LABELS: Record<VoiceState, string> = {
  idle:         'tap to speak',
  listening:    'listening…',
  transcribing: 'transcribing…',
  thinking:     'thinking…',
  speaking:     'tap to interrupt',
};

// Ring config: [size, opacity, animDuration]
const RINGS: [number, number, number][] = [
  [1.6, 0.18, 2.4],
  [2.2, 0.10, 3.2],
  [3.0, 0.05, 4.1],
];

const VoiceTab: React.FC<VoiceTabProps> = ({ addMessage, callApi, loading, agentState, voiceHistory }) => {
  const reduced = useReducedMotion();
  const [voiceState, setVoiceState]   = useState<VoiceState>('idle');
  const [micError, setMicError]       = useState<string | null>(null);
  const [amplitude, setAmplitude]     = useState(0);

  const mediaRecorderRef  = useRef<MediaRecorder | null>(null);
  const chunksRef         = useRef<Blob[]>([]);
  const silenceTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopRecordingRef  = useRef<() => void>(() => {});
  const processAudioRef   = useRef<() => Promise<void>>(async () => {});
  const ampRafRef         = useRef<number>(0);
  const currentAudioRef   = useRef<HTMLAudioElement | null>(null);
  // Unlocked AudioContext — created on first user gesture, reused for all playback
  const unlockedCtxRef    = useRef<AudioContext | null>(null);

  const botState: BotState =
    voiceState === 'listening'    ? 'working'  :
    voiceState === 'transcribing' ? 'thinking' :
    voiceState === 'thinking'     ? 'thinking' :
    voiceState === 'speaking'     ? 'done'     :
    loading ? agentState : 'idle';

  // Orb renders at a fixed 160px and the amplitude pulse is applied as a
  // transform, not as width/height. The size used to be recomputed into layout
  // every frame (width/height in px, transitioning over 0.08s), which forced a
  // reflow of the orb, its rings and the glow on every animation frame.
  // transform scales on the compositor instead — same pulse, no layout thrash.
  const ORB_BASE = 160;
  const orbScale = voiceState === 'speaking'
    ? 1 + amplitude * 0.5
    : voiceState === 'listening'
    ? 1 + amplitude * 0.25
    : 1;
  const orbSize = ORB_BASE;

  const isActive = voiceState !== 'idle' && !loading;

  // ── Unlock audio on first gesture ─────────────────────────────────────────
  const unlockAudio = useCallback(() => {
    if (!unlockedCtxRef.current) {
      unlockedCtxRef.current = new AudioContext();
    }
    if (unlockedCtxRef.current.state === 'suspended') {
      unlockedCtxRef.current.resume();
    }
  }, []);

  // ── Stop currently playing audio ──────────────────────────────────────────
  const stopAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }
    cancelAnimationFrame(ampRafRef.current);
    setAmplitude(0);
  }, []);

  // ── Recording ─────────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    unlockAudio();
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];

      const listenCtx = new AudioContext();
      const analyser  = listenCtx.createAnalyser();
      analyser.fftSize = 256;
      listenCtx.createMediaStreamSource(stream).connect(analyser);

      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        setAmplitude(buf.reduce((s, v) => s + v, 0) / buf.length / 255);
        ampRafRef.current = requestAnimationFrame(tick);
      };
      tick();

      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        cancelAnimationFrame(ampRafRef.current);
        listenCtx.close();
        setAmplitude(0);
        await processAudioRef.current();
      };

      mr.start(100);
      setVoiceState('listening');
      silenceTimerRef.current = setTimeout(() => stopRecordingRef.current(), 30000);
    } catch (err: any) {
      setMicError(err?.name === 'NotAllowedError' ? 'Mic permission denied.' : 'Could not access microphone.');
      setVoiceState('idle');
    }
  }, [unlockAudio]);

  const stopRecording = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    setVoiceState('transcribing');
  }, []);

  const processAudio = useCallback(async () => {
    if (chunksRef.current.length === 0) { setVoiceState('idle'); return; }

    const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });

    try {
      // STT
      setVoiceState('transcribing');
      const arrayBuf = await audioBlob.arrayBuffer();
      const base64   = btoa(new Uint8Array(arrayBuf).reduce((s, b) => s + String.fromCharCode(b), ''));
      const sttRes   = await fetch('/api/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'transcribe', audio_base64: base64, mime_type: audioBlob.type }),
      });
      if (!sttRes.ok) throw new Error(`STT ${sttRes.status}`);
      const { text: transcript } = await sttRes.json();
      if (!transcript?.trim()) { setVoiceState('idle'); return; }

      // LLM — the fast voice path: single call, no context-ir, no tool loop
      setVoiceState('thinking');
      let reply = '';
      try {
        const chatRes = await fetch('/api/voice', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'chat',
            message: transcript.trim(),
            history: voiceHistory || [],
          }),
        });
        if (!chatRes.ok) throw new Error(`chat ${chatRes.status}`);
        const data = await chatRes.json();
        reply = data.reply || '';
      } catch {
        // Fallback to full agent if voice-chat fails
        reply = await callApi(transcript.trim());
      }
      if (!reply) { setVoiceState('idle'); return; }

      // TTS
      setVoiceState('speaking');
      const ttsRes = await fetch('/api/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'speak', text: reply }),
      });
      if (!ttsRes.ok) {
        const e = await ttsRes.json().catch(() => ({}));
        throw new Error(e.error || `TTS ${ttsRes.status}`);
      }

      const wavBuf = await ttsRes.arrayBuffer();
      const blob   = new Blob([wavBuf], { type: 'audio/wav' });
      const url    = URL.createObjectURL(blob);
      const audio  = new Audio(url);
      currentAudioRef.current = audio;

      // Amplitude via MediaElementSource on the unlocked context
      let animFrame = 0;
      try {
        const ctx = unlockedCtxRef.current!;
        if (ctx.state === 'suspended') await ctx.resume();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        const src = ctx.createMediaElementSource(audio);
        src.connect(analyser);
        analyser.connect(ctx.destination);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(buf);
          setAmplitude(buf.reduce((s, v) => s + v, 0) / buf.length / 255);
          animFrame = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        // amplitude optional
      }

      await new Promise<void>((resolve, reject) => {
        audio.onended  = () => resolve();
        audio.onerror  = () => reject(new Error('Playback error'));
        audio.play().catch(reject);
      });

      cancelAnimationFrame(animFrame);
      URL.revokeObjectURL(url);
      currentAudioRef.current = null;
      setAmplitude(0);
      setVoiceState('idle');
    } catch (err: any) {
      if (err?.message !== 'interrupted') {
        addMessage({ type: 'error', text: `Voice error: ${err?.message}` });
      }
      setVoiceState('idle');
      setAmplitude(0);
    }
  }, [callApi, addMessage, voiceHistory]);

  stopRecordingRef.current = stopRecording;
  processAudioRef.current  = processAudio;

  // ── Main tap handler ───────────────────────────────────────────────────────
  const handleTap = useCallback(() => {
    if (voiceState === 'speaking') {
      stopAudio();
      setVoiceState('idle');
      return;
    }
    if (voiceState === 'listening') {
      stopRecordingRef.current();
      return;
    }
    if (voiceState === 'idle' && !loading) {
      startRecording();
    }
  }, [voiceState, loading, stopAudio, startRecording]);

  // Spacebar
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      if (!(e.target instanceof Element) || !e.target.closest('.chatbot-panel__voice') || e.target.closest('button, input, textarea, select, [contenteditable]')) return;
      e.preventDefault();
      if (voiceState === 'speaking') { stopAudio(); setVoiceState('idle'); return; }
      if (voiceState === 'idle' && !loading) startRecording();
    };
    const up = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !(e.target instanceof Element) || !e.target.closest('.chatbot-panel__voice')) return;
      if (voiceState === 'listening') stopRecordingRef.current();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceState, loading]);

  const ringAlpha = voiceState === 'speaking'
    ? 0.15 + amplitude * 0.5
    : voiceState === 'listening'
    ? 0.10 + amplitude * 0.4
    : 0.04;

  return (
    <div className="chatbot-panel__voice" tabIndex={0} aria-label="Voice conversation. Hold Space to speak." style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '28px',
      background: STAGE.bg,
      position: 'relative',
      overflow: 'hidden',
      userSelect: 'none',
    }}>

      {/* Orb + rings */}
      <button type="button" className="chatbot-panel__orb"
        aria-label={voiceState === 'listening' ? 'Stop recording' : voiceState === 'speaking' ? 'Interrupt reply' : 'Start voice recording'}
        aria-pressed={voiceState === 'listening'}
        disabled={loading || voiceState === 'transcribing' || voiceState === 'thinking'}
        onClick={handleTap}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: voiceState === 'transcribing' || voiceState === 'thinking' ? 'default' : 'pointer',
          width: `${orbSize}px`,
          height: `${orbSize}px`,
          transform: `scale(${reduced ? 1 : orbScale})`,
          transition: 'transform 0.08s ease-out',
          willChange: 'transform',
        }}
      >
        {/* Concentric pulsing rings */}
        {RINGS.map(([scale, baseOpacity, dur], i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: `${orbSize * scale}px`,
              height: `${orbSize * scale}px`,
              borderRadius: '50%',
              border: `1px solid ${stageAlpha(STAGE.green, isActive ? baseOpacity + ringAlpha * (1 - i * 0.25) : 0.03)}`,
              animation: isActive ? `voiceRing ${dur}s ease-out infinite ${i * 0.4}s` : 'none',
              transition: 'border-color 0.3s ease',
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* BlobBot orb */}
        <BlobBot
          color={STAGE.green}
          size={orbSize}
          state={botState}
          variant={EOS.variant}
          trackPointer={voiceState === 'idle'}
        />
      </button>

      {/* Label */}
      <div role="status" style={{
        color: voiceState === 'listening' || voiceState === 'speaking' ? STAGE.green : STAGE.muted,
        fontSize: '12px',
        fontFamily: "'JetBrains Mono','Fira Code',monospace",
        letterSpacing: '0.08em',
        transition: 'color 0.3s ease',
        textAlign: 'center',
        lineHeight: 1.8,
      }}>
        {LABELS[voiceState]}
        {voiceState === 'idle' && (
          <span style={{ display: 'block', fontSize: '10px', marginTop: '4px' }}>
            or hold <kbd style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '3px', padding: '0 4px',
              fontFamily: 'inherit',
            }}>Space</kbd>
          </span>
        )}
      </div>

      {micError && (
        <div role="alert" style={{
          color: STAGE.red, fontSize: '11px', textAlign: 'center',
          fontFamily: "'JetBrains Mono','Fira Code',monospace",
          maxWidth: '240px',
        }}>
          {micError}
        </div>
      )}

      <style>{`
        @keyframes voiceRing {
          0%   { transform: scale(1);    opacity: 1; }
          100% { transform: scale(1.35); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default VoiceTab;
